"use client";

import BFLImage from "@repo/shared-ui/BFLImage";
import BFLLink from "@repo/shared-ui/BFLLink";
import Conditional from "@repo/shared-ui/Conditional";
import { getChildByNodeName } from "@repo/shared-utils/common-utils";
import { FC } from "react";
import { IFooter } from "../../interfaces/IFooter.interface";
import FooterAccordion from "./FooterAccordion";
import { handleFooterAppDownloadClickDL } from "./FooterAppDownloadFunction";
import OurCompanies from "./OurCompanies";
import SectionList from "./SectionList";

interface FooterChild {
  nodeName?: string;
  columnTitle?: string;
  sectionTitle?: string;
  linkTitle?: string;
  linkUrl?: string;
  children?: FooterChild[];
}

export interface GlobalFooterProps {
  content: IFooter;
}

const GlobalFooter: FC<GlobalFooterProps> = ({ content }) => {
  const { downloadAppText, copyRightText, corporateLinksTitle, yearText, companyNameImage } =
    content ?? {};

  const corporateSection =
    content?.children?.find((item: FooterChild) => item.nodeName === "corporateSection")
      ?.children ?? [];

  const columnLinks =
    content?.children?.find((item: FooterChild) => item.columnTitle === "Application Forms")
      ?.children ?? [];
  const appDownload = getChildByNodeName(content, "appDownload");
  const corporateLinks = getChildByNodeName(content, "corporateLinks");

  const getSectionByTitle = (title: string) => {
    if (!Array.isArray(content?.children)) return undefined;
    return content.children.find(
      (item: FooterChild) => item.columnTitle === title && "children" in item
    );
  };

  const getColumnLinksBySection = (sectionTitle: string, nodeName: string) => {
    const section = getSectionByTitle(sectionTitle);
    return (
      section?.children?.find(
        (child: FooterChild) => child.nodeName === nodeName && "children" in child
      )?.children ?? []
    );
  };

  const getFirstColumnLinksBySection = (sectionTitles: string[], nodeName: string) => {
    for (const sectionTitle of sectionTitles) {
      const links = getColumnLinksBySection(sectionTitle, nodeName);
      if (links.length > 0) {
        return { sectionTitle, links };
      }
    }

    return { sectionTitle: sectionTitles[0] ?? "", links: [] };
  };

  // Helper for Products Portfolio (flattened structure)
  const extractLinksFromSectionItem = (sectionItem: FooterChild) => {
    const sectionTitle = sectionItem.sectionTitle;
    const sectionLinksNode = (sectionItem.children ?? []).find(
      (c: FooterChild) => c.nodeName === "sectionLinks"
    );
    const links =
      (sectionLinksNode?.children ?? []).map((link: FooterChild) => ({
        linkTitle: link.linkTitle,
        linkUrl: link.linkUrl,
      })) ?? [];
    return {
      sectionTitle,
      links,
    };
  };

  const extractColumnListArr = (item: FooterChild) =>
    item.children?.filter((c: FooterChild) => c.nodeName === "columnList") ?? [];

  const getProductsPortfolioData = () => {
    const section = getSectionByTitle("Products Portfolio");
    if (!section || !Array.isArray(section.children)) return [];
    const columnLinksNode = section.children.find(
      (child: FooterChild) => child.nodeName === "columnLinks" && Array.isArray(child.children)
    );
    if (!columnLinksNode) return [];

    // Flatten the nested structure
    return columnLinksNode?.children?.flatMap((item: FooterChild) => {
      const columnListArr = extractColumnListArr(item);
      return columnListArr.flatMap((columnList: FooterChild) =>
        (columnList.children ?? []).map(extractLinksFromSectionItem)
      );
    });
  };

  const reachUsData = getColumnLinksBySection("Reach Us", "columnLinks");
  const socialMediaIcons = getColumnLinksBySection("Reach Us", "socialMedia") || [];
  const calculatorData = getColumnLinksBySection("Calculators", "columnLinks");
  const importantLinksSection = getFirstColumnLinksBySection(
    ["Important Links", "Important Link", "Legal"],
    "columnLinks"
  );
  const productsData = getProductsPortfolioData() ?? [];

  return (
    <div className="bg-bfl-blue">
      <FooterAccordion
        data-testid="footer-accordion"
        loanLinks={columnLinks}
        reachUsData={reachUsData}
        socialMediaIcons={socialMediaIcons}
        calculatorData={calculatorData}
        importantLink={importantLinksSection.links}
        importantLinkTitle={importantLinksSection.sectionTitle}
        productsData={productsData}
      />
      <div className="g-4 flex flex-wrap justify-between p-4 lg:gap-0">
        {corporateSection.map((sectionItem: FooterChild, index: number) => (
          <SectionList
            key={sectionItem?.columnTitle ?? `corporate-section-${index}`}
            data-testid="section-list"
            data={
              (sectionItem?.children?.find((child: FooterChild) => child.nodeName === "columnList")
                ?.children ?? []) as unknown as { sectionTitle: string; subTitleText?: string }[]
            }
            className="w-[40%]! md:w-[18%]!"
          />
        ))}
        <OurCompanies
          corporateLinksTitle={corporateLinksTitle ?? ""}
          corporateLinks={corporateLinks}
        />
        <div className="flex w-[100%] flex-col item-center md:items-start pt-[50px] md:w-[18%]! md:pt-0">
          <BFLImage
            className="hidden pt-[20px] pb-[30px] md:!block"
            src={companyNameImage ?? ""}
            alt="Bajaj"
            priority={false}
            loading="lazy"
            unoptimized={true}
            width={84}
            height={30}
          />
          <div className="mb-2 flex justify-center gap-8 text-[32px] max-h-[36px]">
            {appDownload?.map((_icon: any, index: number) => (
              <BFLLink
                key={"appDownload-" + index}
                href={_icon?.platformDownloadUrl}
                aria-label={`Download Bajaj Finserv app on ${_icon.platformTitleText || "mobile"}`}
                onClick={() => {
                  // Track analytics for app download click
                  handleFooterAppDownloadClickDL(
                    "", // loginStatus
                    _icon?.platformDownloadUrl ?? "", // destination_url
                    _icon.platformTitleText ?? "", // platform (Android/iOS)
                    _icon.platformTitleText ?? "" // cta_text
                  );
                }}
              >
                <>
                  <Conditional if={_icon.platformTitleText === "Android"}>
                    <i className="bf-icon-android-fill inline-block h-[32px] w-[32px] leading-none"></i>
                  </Conditional>
                  <Conditional if={_icon.platformTitleText === "iOS"}>
                    <i className="bf-icon-apple-fill inline-block h-[32px] w-[32px] leading-none"></i>
                  </Conditional>
                </>
              </BFLLink>
            ))}
          </div>
          <h3 data-testid="download-text" className="mb-4 text-center text-sm md:mb-0 md:text-base">
            {downloadAppText}
          </h3>
        </div>
        <h3
          data-testid="copyright-text"
          className="flex w-full justify-center text-[12px] md:!justify-end md:pb-0 font-medium"
        >
          {yearText} {new Date().getFullYear()}
          {copyRightText}
        </h3>
      </div>
    </div>
  );
};
export default GlobalFooter;
